// SingleProductView.tsx
import { useState } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import {
  EnumOrderPostStatus,
  enumOrderPostStatusToStringMapping,
  MissingPhoto,
} from 'Interfaces/IGlobalTypeConfig';
import { GetColorByName } from 'functions/GetColorByName';
import { useTranslation } from 'react-i18next';
import { CompanyProductEditDrawer } from './Drawers/CompanyProductEditDrawer';
import EditIcon from '@mui/icons-material/Edit';
import { TicketProductViewInterface } from 'pages/Tickets/Components/TicketStyleConfig';

type Props = {
  index?: number;
  product: TicketProductViewInterface;
  editable?: boolean;
  companyId?: string;
  purchaserId?: string;
  updateStatus: (status: EnumOrderPostStatus, id?: string | null) => void;
};
const ShowColor = (status: number) => {
  switch (status) {
    case 0:
      return 'rgba(255, 0, 0, 0.5)';
    case 1:
      return 'rgba(255, 166, 0, 0.5)';
    case 2:
      return 'rgba(255, 166, 0, 0.5)';
    case 3:
      return 'rgba(255, 166, 0, 0.5)';
    case 4:
      return 'rgba(0, 255, 174, 0.5)';
    case 5:
      return 'rgba(0, 255, 102, 0.5)';
    default:
      return 'primary';
  }
};

export const SingleTicketProductView = ({
  product,
  editable = false,
  updateStatus,
  companyId,
  purchaserId,
}: Props) => {
  const { t } = useTranslation();
  // const [openDrawer, setOpenDrawer] = useState(false);
  if (!product || product.product === null) {
    console.log('1. ERROR:', product);
    console.warn('SingleProductView: product is null/undefined');
    return null; // eller en enkel fallback-komponent
  }

  console.log('1. SingleTicketProductView:', product);
  /*  let productVariant =
    product?.product.paymentCycle === 0 ? 'Investment' : 'Agreement'; */
  let billingCycle =
    product.product?.bindingPeriod !== 0 ? product.product?.bindingPeriod : 0;
  let paymentCycle =
    product.product?.paymentCycle !== 0 ? product.product?.paymentCycle : 0;
  let cycle =
    product.product?.paymentCycle === 0
      ? ''
      : `  ${paymentCycle} / ${billingCycle}`;

  /*  const handleFieldChange = (field: string, value: string | number) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  }; */
  /*  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  }; */

  return (
    <Grid
      container
      flexGrow={1}
      direction={'row'}
      /*  onClick={() => {
        handleOpenDrawer();
      }} */
      sx={{
        mb: 1,
        borderLeft: 8,
        borderRight: 3,
        borderRadius: 3,
        borderColor: GetColorByName(product.product?.productType?.name || '-')
          .background,
        backgroundColor: !product.vendorProductUrl
          ? 'primary.main'
          : 'primary.main',
        ':hover': {
          cursor: 'pointer',
          filter: 'brightness(110%)',
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <Grid
        item
        sx={{
          p: 0.3,
          mt: 0,
          width: 88,
          height: 88,
          borderTopLeftRadius: '3px',
          borderBottomLeftRadius: '3px',
          backgroundColor: 'primary.light',
          display: 'flex',
        }}
      >
        <img
          src={
            product?.product?.url
              ? product.product.url
              : MissingPhoto.PRODUCT_URL
          }
          style={{
            width: '100%',
            height: '100%',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
            objectFit: 'scale-down',
          }}
          alt={'logo'}
        />
      </Grid>

      <Grid
        item
        sx={{
          pl: 1,
          height: 88,
          flexGrow: 1,
        }}
      >
        <Stack direction={'column'}>
          <Typography
            sx={{
              letterSpacing: 1,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              fontSize: 18,
            }}
          >
            {product?.product?.name} <span>{cycle}</span>
          </Typography>

          <Grid
            container
            direction={'row'}
            sx={{
              height: 56,
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {product.product?.features &&
              product.product?.features.map((_features: any, index: number) => {
                return (
                  <Grid item key={index} xs={6}>
                    <Grid container direction={'row'}>
                      <Grid item xs={6}>
                        <Typography
                          noWrap
                          sx={{
                            width: 110,
                            fontSize: 12,
                            letterSpacing: 1,
                            fontWeight: 'bold',
                            textAlign: 'left',
                            color: 'primary.text',
                          }}
                        >
                          {_features.featureName}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 12,
                            letterSpacing: 1,
                            fontWeight: 'normal',
                            textAlign: 'left',
                            color: 'primary.text',
                          }}
                        >
                          {_features.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
        </Stack>
      </Grid>

      <Grid item xs={4} sx={{}}>
        <Stack direction={'column'}>
          <Typography
            noWrap
            sx={{
              pr: 1,
              letterSpacing: 3,
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 'normal',
              textTransform: 'capitalize',
              fontSize: 16,
              backgroundColor: ShowColor(product?.status ?? 0),
            }}
          >
            {enumOrderPostStatusToStringMapping[
              product.status as EnumOrderPostStatus
            ]?.toUpperCase() || ''}
          </Typography>
          <Typography
            sx={{
              height: 64,
              pr: 1,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              alignContent: 'stretch',
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
              color: 'secondary.text',
            }}
          >
            {(product?.retailPrice ?? 0).toFixed(2)}
          </Typography>
        </Stack>
      </Grid>
      <Grid
        item
        xs={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          alignContent: 'center',
        }}
      >
        <Stack direction={'column'} spacing={1} alignItems='center'>
          <Grid item sx={{ height: 32, width: 32 }}>
            {product.status >= 4 && product.status <= 5 && (
              <CompanyProductEditDrawer
                product={product}
                companyId={companyId || ''}
                buttonIcon={<EditIcon sx={{ color: 'white' }} />}
                buttonContext=''
                updateStatus={updateStatus}
                //isEnabled={editable}
                //open={openDrawer}
              />
            )}
          </Grid>
          {/* <Chip
            label={product?.quantity || 0}
            sx={{
              fontSize: 16,
              fontWeight: 'bold',
              height: 32,
            }}
          /> */}
        </Stack>
      </Grid>
    </Grid>
  );
};
